import React, { useState, useEffect, useMemo } from 'react';
import { find } from 'lodash';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box } from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';
import { useEffectAsync } from '@availity/hooks';
import { TokenStorage } from '@forgerock/javascript-sdk';

import { useAppContext } from '@/context';
import { checkNavigation, JOURNEY_LOGIN, ONPREM_COOKIE_VALUES } from '@/global';
import { useFRStep } from '@/hooks';
import {
  EmailSent,
  ErrorPage,
  LoginStart,
  NearExpiredPassword,
  NewEmail,
  NullEmail,
  PasswordReset,
  PasswordResetSuccess,
  SecurityQuestionsSetup,
  TwoFAComplete,
  TwoFAConfirmNumber,
  TwoFAEnrollmentSelectMethod,
  TwoFAHotpEnrollment,
  TwoFAInputToken,
  TwoFAInputBackupCodeV2,
  TwoFAAuthenticatorInputToken,
  TwoFAAuthenticatorSetup,
  UnverifiedEmail,
  UserAgreementPage,
  VerifiedEmail,
  TwoFAWelcome,
  TwoFACompleteWarning,
  TwoFAChooseMethod,
} from '@/pages';

// construct the home page url in event we need to redirect to it if onprem session is found
const { host = '', href: currentHref } = window.location;
const lowercaseHost = host?.toLowerCase();
const specificEnv = getSpecificEnv();
const avUrlPrefix = specificEnv === 'prod' ? 'apps' : `${specificEnv}-apps`;
const windowHasNav = currentHref.indexOf('/public/apps/home') >= 0; // determine if location has the availity nav; check for 'loadapp' in the url
const hasDotNet = lowercaseHost.indexOf('.net') >= 0; // determine if the portal loaded in .net or .com
const domainSuffix = hasDotNet ? '.net' : '.com'; // set the domain suffix
const baseAvUrl = `https://${avUrlPrefix}.availity${domainSuffix}`; // set the base av url
const avHomeUrl = windowHasNav ? `${baseAvUrl}/public/apps/dashboard` : `${baseAvUrl}/public/apps/home/#!/`; // home page url

function Login({ journeyConstant = JOURNEY_LOGIN }) {
  const {
    setIsAuthenticated,
    setEmail,
    setUsername,
    securityQuestionsOptions,
    setSecurityQuestionsOptions,
    queryParams = {},
  } = useAppContext();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState();
  const [hasEvaluatedOnPremCookie, setHasEvaluatedOnPremCookie] = useState(false);

  // check for on-prem cookie to see if onprem session is still in place. if present, will need to re-direct to the homepage
  useEffect(() => {
    const cookies = document.cookie;
    let hasOnPremCookie = false;
    for (const cookieVal of ONPREM_COOKIE_VALUES) {
      if (cookies.indexOf(cookieVal) >= 0) {
        hasOnPremCookie = true;
      }
    }

    setHasEvaluatedOnPremCookie(true);

    if (hasOnPremCookie) {
      window.top.location.assign(avHomeUrl);
    }
  }, []);

  // handle initialization of the application and process query params;
  useEffectAsync(async () => {
    let isAuthenticated;
    try {
      isAuthenticated = !!(await TokenStorage.get());
    } catch (error) {
      console.log(error);
    }

    // const prefersDarkTheme = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const email = window.sessionStorage.getItem('sdk_email');
    const username = window.sessionStorage.getItem('sdk_username');

    // can enable this if we support dark theme in the future.
    // if (prefersDarkTheme) {
    //   document.body.classList.add('cstm_bg-dark', 'bg-dark');
    // }

    setIsAuthenticated(!!isAuthenticated);
    setEmail(email);
    setUsername(username);
  }, []);

  const { authIndexValue = '', composite_advice } = queryParams;

  // need to do a check for the composite_advice param. if present, this overrides the base login - and the user must do a 2FA.
  if (composite_advice) {
    navigate('/2fa-challenge');
  }

  // process authIndexValue in event that the UI should navigate to a different journey
  const journeyId = useMemo(() => authIndexValue?.toLowerCase(), [authIndexValue]);

  // call utility function to check if we need to navigate to a different route
  useEffect(() => {
    if (journeyId) {
      checkNavigation(journeyId);
    }
  }, [journeyId]);

  // get FR step info from custom hook
  // NOTE: updating the currentStep 'should' trigger fetching of the stepToRender
  // currentStep should be changed on form submission
  const { stepToRender, errorCode } = useFRStep(currentStep, journeyConstant);

  const pageNodeToRender = useMemo(() => {
    let component;

    if (stepToRender) {
      const { payload = {} } = stepToRender;
      const { callbacks = [], stage = '' } = payload;

      // extract security questions options
      if (callbacks[0]?.type?.toLowerCase() === 'kbacreatecallback' && !securityQuestionsOptions) {
        const { output = [] } = callbacks[0];
        const outputQuestionObj = find(output, (entry) => entry.name === 'predefinedQuestions', {});
        const { value: optionsArray } = outputQuestionObj;

        // build out options for select field
        const optionsToUse = [];
        for (const question of optionsArray) {
          optionsToUse.push({
            label: question,
            value: question,
          });
        }

        setSecurityQuestionsOptions([...optionsToUse]);
      }

      // first check for a suspended text output callback; in this case the email sent node should render
      // a stage name should not be present in this case
      if (callbacks?.length > 0) {
        const callbackToCheck = callbacks[0];

        if (callbackToCheck?.type?.toLowerCase() === 'suspendedtextoutputcallback') {
          component = <EmailSent key="emailSent" stepToRender={stepToRender} />;
        }
      }

      // check for the main login start node
      switch (stage) {
        case 'LoginpageUnique001': {
          component = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'ForgottenPassword002': {
          component = (
            <PasswordReset stepToRender={stepToRender} setCurrentStep={setCurrentStep} errorCode={errorCode} />
          );
          break;
        }
        case 'ForgottenPassword003': {
          component = <PasswordResetSuccess stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'ForgottenUsername002': {
          component = <EmailSent key="emailSent" stepToRender={stepToRender} />;
          break;
        }
        case 'LoginExpiringPassword': {
          component = <NearExpiredPassword stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'Login-ShowTermsOfService': {
          component = <UserAgreementPage stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-Welcome': {
          component = <TwoFAWelcome stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-SelectMethod': {
          component = <TwoFAEnrollmentSelectMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-OATHAppRegister': {
          component = <TwoFAAuthenticatorSetup stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-AppOATHVerify':
        case 'MFA-AppOATHVerify': {
          component = (
            <TwoFAAuthenticatorInputToken
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
            />
          );
          break;
        }
        case 'MFAEnrollment-EnterSMSNumber': {
          component = (
            <TwoFAHotpEnrollment
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
              hotpType="sms"
            />
          );
          break;
        }
        case 'MFAEnrollment-EnterTTSNumber': {
          component = (
            <TwoFAHotpEnrollment
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
              hotpType="tts"
            />
          );
          break;
        }
        case 'MFA-SelectMethod': {
          component = <TwoFAChooseMethod stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'MFAEnrollment-ConfirmSMSNumber': {
          component = <TwoFAConfirmNumber stepToRender={stepToRender} setCurrentStep={setCurrentStep} hotpType="sms" />;
          break;
        }
        case 'MFAEnrollment-ConfirmTTSNumber': {
          component = <TwoFAConfirmNumber stepToRender={stepToRender} setCurrentStep={setCurrentStep} hotpType="tts" />;
          break;
        }
        case 'MFAEnrollment-SMSOTPVerify':
        case 'MFAEnrollment-TTSOTPVerify':
        case 'NAG-MFA-SMSOTPVerify':
        case 'NAG-MFA-TTSOTPVerify':
        case 'MFA-SMSOTPVerify':
        case 'MFA-TTSOTPVerify': {
          component = (
            <TwoFAInputToken stepToRender={stepToRender} setCurrentStep={setCurrentStep} errorCode={errorCode} />
          );
          break;
        }
        case 'MFA-BackupVerify':
        case 'MFAEnrollment-BackupVerify': {
          component = (
            <TwoFAInputBackupCodeV2 stepToRender={stepToRender} setCurrentStep={setCurrentStep} errorCode={errorCode} />
          );
          break;
        }
        case 'MFAEnrollment-CompleteWithCodes':
        case 'MFA-CompleteWithCodes': {
          component = <TwoFAComplete stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes />;
          break;
        }
        case 'MFAEnrollment-CompleteWithoutCodes':
        case 'MFA-CompleteWithoutCodes': {
          component = (
            <TwoFAComplete stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes={false} />
          );
          break;
        }
        case 'NAG-MFAEnrollment-CompleteWithCodes':
        case 'NAG-MFA-CompleteWithCodes': {
          component = (
            <TwoFACompleteWarning stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes />
          );
          break;
        }
        case 'NAG-MFAEnrollment-CompleteWithoutCodes':
        case 'NAG-MFA-CompleteWithoutCodes': {
          component = (
            <TwoFACompleteWarning stepToRender={stepToRender} setCurrentStep={setCurrentStep} showBackupCodes={false} />
          );
          break;
        }
        case 'avSimpleKBA_Login': {
          component = <LoginStart stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'avSimpleKBA_001':
        case 'KBA-EnterKBA': {
          component = (
            <SecurityQuestionsSetup
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              journeyId={JOURNEY_LOGIN}
            />
          );
          break;
        }
        case 'unverifiedemail': {
          component = (
            <UnverifiedEmail key="unverifiedEmail" stepToRender={stepToRender} setCurrentStep={setCurrentStep} />
          );
          break;
        }
        case 'emailverified': {
          component = <VerifiedEmail key="verifiedEmail" stepToRender={stepToRender} setCurrentStep={setCurrentStep} />;
          break;
        }
        case 'noemail': {
          component = (
            <NullEmail
              key="noEmail"
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
            />
          );
          break;
        }
        case 'changeemail': {
          component = (
            <NewEmail
              key="newEmail"
              stepToRender={stepToRender}
              setCurrentStep={setCurrentStep}
              errorCode={errorCode}
            />
          );
          break;
        }
        case 'emailsent': {
          component = <EmailSent key="emailSent" stepToRender={stepToRender} />;
          break;
        }
        case 'ErrorPage': {
          component = <ErrorPage key="errorPage" />;
          break;
        }
        default:
      }
    }

    return component;
  }, [stepToRender]);

  return hasEvaluatedOnPremCookie ? <Box role="main">{pageNodeToRender}</Box> : null;
}

Login.propTypes = {
  journeyConstant: PropTypes.string,
};

export default Login;
